import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLock, faTrophy, faRocket, faServer, faArrowRight, faArrowLeft, faKey, faMicrophone } from '@fortawesome/free-solid-svg-icons';
import PlatformVideo from './PlatformVideo';
import {
  PlatformContainer,
  PlatformHeader,
  PlatformIcon,
  PlatformTitle,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  ProgressText,
  StepsList,
  StepItem,
  StepNumber,
  StepContent,
  StepText,
  StepLink,
  CompletedCheckmark,
  StepToggle,
  StepDescription,
  TechCircle,
  SuccessMessage,
  AnimatedBorder,
  TechGrid,
  DataStream,
  NavigationButtons,
  NavigationButton
} from './PlatformStyles';

const VapiPlatform = ({ completedSteps, onStepComplete, onNavigateToNextPlatform, onUpdateCredential, platformColor, activeStep, onUpdateActiveStep }) => {
  const [progress, setProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [credentials, setCredentials] = useState({
    vapiApiKey: ''
  });
  
  const platformUrl = 'https://dashboard.vapi.ai/';
  
  const steps = [
  {
    title: 'Configurar Vapi e Fornecer API Key',
    description: 'Acesse o site oficial da VAPI. Crie sua conta, adicione seu cartão e pegue a API KEY em "ORG Settings"',
    icon: faMicrophone,
    isCredential: true,
    inputLabel: 'API Key',
    inputName: 'vapiApiKey',
    hasVapiLink: true
  }
];
  
  useEffect(() => {
    const completed = completedSteps.filter(step => step).length;
    const total = completedSteps.length;
    setProgress(Math.round((completed / total) * 100));
    
    if (completed === total && total > 0) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [completedSteps]);
  
  const handleCompleteStep = () => {
    // Marcar a tarefa atual como concluída
    onStepComplete(activeStep);
    
    // Definir que está em transição
    setIsTransitioning(true);
    
    // Adicionar um delay maior para permitir que o usuário veja a animação
    setTimeout(() => {
      // Avançar automaticamente para o próximo passo após marcar como concluído
      if (activeStep < steps.length - 1) {
        onUpdateActiveStep(activeStep + 1);
      }
      
      // Finalizar a transição
      setIsTransitioning(false);
    }, 800);
  };
  
  const handleNextStep = () => {
    // Evitar múltiplos cliques durante a transição
    if (isTransitioning) return;
    
    // Verificar se os campos de credenciais estão preenchidos no último passo
    if (activeStep === steps.length - 1) {
      const { vapiApiKey } = credentials;
      if (!vapiApiKey) {
        // Alertar o usuário que precisa preencher todos os campos
        console.warn('Por favor, preencha a API Key da Vapi antes de prosseguir.');
        return;
      }
    }
    
    // Definir que está em transição
    setIsTransitioning(true);
    
    // Marcar a tarefa atual como concluída
    if (!completedSteps[activeStep]) {
      onStepComplete(activeStep);
    }
    
    // Adicionar um delay maior para permitir que o usuário veja a animação
    setTimeout(() => {
      // Se for o último passo, apenas mostrar a mensagem de parabéns
      if (activeStep === steps.length - 1) {
        // Não redirecionar para a próxima plataforma
        // Apenas completar o último passo
        if (onNavigateToNextPlatform) {
          onNavigateToNextPlatform();
        }
      } else {
        // Caso contrário, avançar para o próximo passo
        onUpdateActiveStep(activeStep + 1);
      }
      
      // Finalizar a transição
      setIsTransitioning(false);
    }, 800);
  };
  
  const handlePrevStep = () => {
    // Evitar múltiplos cliques durante a transição
    if (isTransitioning) return;
    
    // Definir que está em transição
    setIsTransitioning(true);
    
    if (activeStep > 0) {
      // Adicionar uma animação suave para voltar
      setTimeout(() => {
        onUpdateActiveStep(activeStep - 1);
        
        // Finalizar a transição
        setIsTransitioning(false);
      }, 150);
    } else {
      // Finalizar a transição se não houver navegação
      setIsTransitioning(false);
    }
  };
  
  const isAllComplete = progress === 100;
  const currentStep = steps[activeStep];
  
  return (
    <PlatformContainer>
      <TechGrid />
      <DataStream />
      
      
      
      {/* Componente de vídeo para a plataforma */}
      <PlatformHeader>
  <PlatformIcon color={platformColor}>
    <FontAwesomeIcon icon={faMicrophone} />
  </PlatformIcon>
  <PlatformTitle color={platformColor}>Vapi</PlatformTitle>
</PlatformHeader>

<PlatformVideo 
  platformId="vapi" 
  platformColor={platformColor} 
/>

<StepsList>
  <StepItem 
    key={activeStep} 
    completed={completedSteps[activeStep]}
    active={true}
  >
    <AnimatedBorder active={true} color={platformColor} />
          
          <StepContent>
            <StepText>{currentStep.title}</StepText>
            <StepDescription>{currentStep.description}</StepDescription>
            {currentStep.hasVapiLink && (
              <StepLink 
                href={platformUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                color={platformColor}
              >
                Acessar Vapi
              </StepLink>
            )}
            {currentStep.isCredential && (
              <div style={{ marginTop: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '5px', 
                  color: 'rgba(255, 255, 255, 0.8)' 
                }}>
                  {currentStep.inputLabel}
                </label>
                <input 
                  type="text" 
                  name={currentStep.inputName}
                  value={credentials[currentStep.inputName] || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCredentials(prev => ({
                      ...prev,
                      [currentStep.inputName]: value
                    }));
                    onUpdateCredential && onUpdateCredential('vapi', currentStep.inputName, value);
                  }}
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    background: 'rgba(0, 0, 0, 0.3)', 
                    border: `1px solid ${platformColor}`, 
                    borderRadius: '4px', 
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
            )}
          </StepContent>
          
          
        </StepItem>
      </StepsList>
      
      <NavigationButtons>
        <NavigationButton 
          onClick={handlePrevStep} 
          disabled={activeStep === 0 || isTransitioning}
          style={{ 
            opacity: (activeStep === 0 || isTransitioning) ? 0.5 : 1,
            cursor: (activeStep === 0 || isTransitioning) ? 'not-allowed' : 'pointer',
            background: 'transparent',
            color: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Anterior
        </NavigationButton>
        
        <NavigationButton 
          onClick={handleNextStep} 
          disabled={isTransitioning}
          style={{ 
            opacity: isTransitioning ? 0.5 : 1,
            cursor: isTransitioning ? 'not-allowed' : 'pointer',
            background: `linear-gradient(to right, ${platformColor}, ${platformColor}CC)`,
            color: 'white',
            border: 'none',
            padding: '0.8rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {activeStep === steps.length - 1 ? 'Próxima Plataforma' : 'Próximo'} <FontAwesomeIcon icon={faArrowRight} />
        </NavigationButton>
      </NavigationButtons>
      
      {isAllComplete && (
        <SuccessMessage color={platformColor}>
          <FontAwesomeIcon icon={faTrophy} />
          <span>
            <strong>Concluído!</strong>
          </span>
        </SuccessMessage>
      )}
    </PlatformContainer>
  );
};

export default VapiPlatform;
